<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">CBHI Plans Management</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboard</a></li>
                    <li class="breadcrumb-item active">Plans Management</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- CBHI Pricing Rules Info Banner -->
          <div class="alert alert-info border-left-info">
            <div class="d-flex align-items-start">
              <i class="bx bx-info-circle fs-3 me-3"></i>
              <div>
                <h5 class="mb-2"><strong>Ethiopian CBHI Pricing Model</strong></h5>
                <ul class="mb-0 small">
                  <li><strong>Principal Member:</strong> Pays annual premium based on selected plan</li>
                  <li><strong>Immediate Family (FREE):</strong> Spouse + all children under 18 years</li>
                  <li><strong>Additional Members:</strong> Extra spouses, adult children (18+), parents, siblings - charged at <strong>1/5 (20%)</strong> of principal amount</li>
                  <li><strong>Lower Plan:</strong> Completely FREE for poverty-line families (identified by HEWs)</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Total Plans</p>
                      <h4 class="mb-0">{{ planStats.totalPlans }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-shield-check text-primary fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Active Plans</p>
                      <h4 class="mb-0">{{ planStats.activePlans }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-check-circle text-success fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Total Members</p>
                      <h4 class="mb-0">{{ planStats.totalMembers }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-group text-info fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Potential Revenue</p>
                      <h4 class="mb-0">ETB {{ formatCurrency(planStats.totalRevenue) }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-money text-warning fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Coverage Configuration Alert -->
          <div v-if="!coverageConfigsInitialized" class="alert alert-warning">
            <i class="bx bx-info-circle me-2"></i>
            <strong>System Configuration Required:</strong> Coverage thresholds need to be initialized.
            <button class="btn btn-sm btn-warning ms-2" @click="initializeConfigs">
              <i class="bx bx-cog me-1"></i>Initialize Now
            </button>
          </div>

          <!-- Plans Management Card -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col">
                      <h4 class="card-title mb-0">Insurance Plans</h4>
                      <p class="text-muted mb-0">Manage CBHI insurance plans and coverage options</p>
                    </div>
                    <div class="col-auto">
                      <div class="d-flex gap-2">
                        <button class="btn btn-outline-primary" @click="goToServicesManagement">
                          <i class="bx bx-list-ul me-1"></i>
                          Manage Services
                        </button>
                        <button class="btn btn-outline-info" @click="openConfigModal">
                          <i class="bx bx-cog me-1"></i>
                          Coverage Settings
                        </button>
                        <div class="dropdown">
                          <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                            <i class="bx bx-filter-alt me-1"></i>
                            {{ getFilterText() }}
                          </button>
                          <ul class="dropdown-menu">
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('all')">All Plans</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('active')">Active Only</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('inactive')">Inactive Only</a></li>
                            <li><hr class="dropdown-divider"></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('basic')">Basic Plans</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('standard')">Standard Plans</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('premium')">Premium Plans</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('family')">Family Plans</a></li>
                          </ul>
                        </div>
                        <button class="btn btn-success" @click="addNewPlan">
                          <i class="bx bx-plus-circle me-1"></i>
                          Add New Plan
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Search and Per Page Controls -->
                  <div class="row mb-3">
                    <div class="col-md-4">
                      <div class="search-box">
                        <div class="position-relative">
                          <input
                              v-model="searchTerm"
                              @input="handleSearch"
                              type="text"
                              class="form-control"
                              placeholder="Search plans..."
                          >
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-8 text-end">
                      <div class="d-flex justify-content-end align-items-center gap-2">
                        <label class="mb-0 text-muted">Show:</label>
                        <select v-model="perPage" @change="handlePerPageChange" class="form-select form-select-sm" style="width: auto;">
                          <option :value="10">10</option>
                          <option :value="25">25</option>
                          <option :value="50">50</option>
                          <option :value="100">100</option>
                        </select>
                        <span class="text-muted">entries</span>
                        <button @click="refreshPlans" class="btn btn-light btn-sm ms-2" :disabled="loading">
                          <i class="bx bx-refresh" :class="{ 'spin-animation': loading }"></i> Refresh
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3">Loading plans...</p>
                  </div>

                  <!-- Plans Table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover border table-striped">
                      <thead class="table-light">
                      <tr>
                        <th scope="col" style="width: 50px;">No.</th>
                        <th scope="col">Plan Details</th>
                        <th scope="col">Pricing Structure</th>
                        <th scope="col">Family Coverage</th>
                        <th scope="col">Eligibility</th>
                        <th scope="col" class="text-center">Status</th>
                        <th scope="col" class="text-center" style="width: 180px;">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(plan, index) in paginatedPlans" :key="plan.plan_id">
                        <td>{{ (currentPage - 1) * perPage + index + 1 }}.</td>

                        <!-- Plan Details Column -->
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="plan-icon-wrapper me-3" :class="getPlanIconWrapperClass(plan.plan_type)">
                              <i :class="getPlanIcon(plan.plan_type)" class="plan-icon"></i>
                            </div>
                            <div>
                              <h6 class="mb-0 fw-bold">
                                {{ plan.plan_name }}
                                <span v-if="plan.is_free" class="badge bg-success ms-2">FREE</span>
                              </h6>
                              <p class="text-muted mb-0 small">Code: {{ plan.plan_code }}</p>
                              <span class="badge" :class="getPlanTypeBadgeClass(plan.plan_type)">
                                {{ formatPlanType(plan.plan_type) }}
                              </span>
                              <div v-if="plan.category_name" class="mt-1">
                                <span class="badge bg-light text-dark">{{ plan.category_name }}</span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <!-- Pricing Structure Column -->
                        <td>
                          <div class="pricing-details">
                            <div class="mb-2">
                              <strong class="text-primary fs-5">ETB {{ formatCurrency(plan.primary_annual_amount) }}</strong>
                              <small class="text-muted d-block">Principal Member / Year</small>
                            </div>

                            <div v-if="!plan.is_free" class="border-top pt-2">
                              <div class="d-flex justify-content-between align-items-center mb-1">
                                <span class="text-muted small">Additional Member Rate:</span>
                                <strong class="text-info">{{ (plan.additional_member_rate * 100).toFixed(0) }}%</strong>
                              </div>
                              <div class="d-flex justify-content-between align-items-center">
                                <span class="text-muted small">Cost per Extra Member:</span>
                                <strong class="text-success">ETB {{ formatCurrency(plan.additional_member_cost) }}</strong>
                              </div>
                            </div>

                            <div v-else class="text-center py-2 bg-light rounded">
                              <i class="bx bx-check-circle text-success me-1"></i>
                              <strong class="text-success">Completely FREE</strong>
                              <small class="text-muted d-block">For poverty-line families</small>
                            </div>
                          </div>
                        </td>

                        <!-- Family Coverage Column -->
                        <td>
                          <div class="family-coverage-info">
                            <div class="mb-2">
                              <span class="badge bg-success mb-1">
                                <i class="bx bx-check me-1"></i>FREE Immediate Family
                              </span>
                            </div>

                            <ul class="list-unstyled small mb-0">
                              <li class="mb-1">
                                <i class="bx bx-user text-success me-1"></i>
                                <strong>Spouse:</strong> <span class="text-success">FREE</span>
                              </li>
                              <li class="mb-1">
                                <i class="bx bx-group text-success me-1"></i>
                                <strong>Children &lt;18:</strong> <span class="text-success">FREE (All)</span>
                              </li>
                              <li class="mb-1" v-if="!plan.is_free">
                                <i class="bx bx-user-plus text-info me-1"></i>
                                <strong>Extra Members:</strong> <span class="text-info">ETB {{ formatCurrency(plan.additional_member_cost) }}</span>
                              </li>
                              <li v-else>
                                <i class="bx bx-heart text-success me-1"></i>
                                <strong>All Family:</strong> <span class="text-success">FREE</span>
                              </li>
                            </ul>

                            <div v-if="!plan.is_free" class="mt-2 pt-2 border-top">
                              <small class="text-muted fst-italic">
                                <i class="bx bx-info-circle me-1"></i>
                                Extra: Additional spouses, adult children 18+, relatives
                              </small>
                            </div>
                          </div>
                        </td>

                        <!-- Eligibility Column -->
                        <td>
                          <div v-if="plan.eligibility_text" class="eligibility-info">
                            <div class="alert alert-light border-left mb-0 p-2">
                              <small>{{ plan.eligibility_text }}</small>
                            </div>
                          </div>
                          <div v-else class="text-muted small">
                            Available to all eligible citizens
                          </div>

                          <div class="mt-2">
                            <small class="text-muted">
                              <i class="bx bx-calendar me-1"></i>
                              Effective: {{ formatDate(plan.effective_from) }}
                            </small>
                          </div>
                        </td>

                        <!-- Status Column -->
                        <td class="text-center">
                          <span class="badge fs-6" :class="getStatusBadgeClass(plan)">
                            <i :class="getStatusIcon(plan)"></i>
                            {{ getPlanStatus(plan) }}
                          </span>
                        </td>

                        <!-- Actions Column -->
                        <td>
                          <div class="d-flex gap-2 justify-content-center">
                            <button class="btn btn-sm btn-outline-secondary" @click="editPlan(plan)" title="Edit Plan">
                              <i class="bx bx-edit"></i>
                            </button>
                            <button class="btn btn-sm" :class="plan.is_active ? 'btn-outline-danger' : 'btn-outline-success'" @click="togglePlanStatus(plan)" :title="plan.is_active ? 'Deactivate' : 'Activate'">
                              <i :class="plan.is_active ? 'bx bx-x-circle' : 'bx bx-check-circle'"></i>
                            </button>
                            <button class="btn btn-sm btn-outline-danger" @click="deletePlan(plan)" title="Delete Plan">
                              <i class="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>

                    <!-- Empty State -->
                    <div v-if="paginatedPlans.length === 0" class="text-center py-5">
                      <i class="bx bx-shield fs-1 text-muted"></i>
                      <p class="text-muted mt-3">No plans found matching your filters</p>
                      <button class="btn btn-primary" @click="setFilter('all')">
                        <i class="bx bx-refresh me-1"></i>Show All Plans
                      </button>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPlans > 0" class="row mt-3">
                      <div class="col-sm-12 col-md-5">
                        <div class="dataTables_info">
                          Showing {{ showingFrom }} to {{ showingTo }} of {{ totalPlans }} entries
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-7">
                        <div class="dataTables_paginate paging_simple_numbers">
                          <ul class="pagination justify-content-end mb-0">
                            <li class="paginate_button page-item" :class="{ disabled: currentPage === 1 }">
                              <a href="javascript:void(0);" @click.prevent="goToPage(1)" class="page-link">
                                <i class="bx bx-chevrons-left"></i>
                              </a>
                            </li>
                            <li class="paginate_button page-item previous" :class="{ disabled: currentPage === 1 }">
                              <a href="javascript:void(0);" @click.prevent="previousPage" class="page-link">Previous</a>
                            </li>
                            <li
                                v-for="page in visiblePages"
                                :key="page"
                                class="paginate_button page-item"
                                :class="{ active: page === currentPage }"
                            >
                              <a href="javascript:void(0);" @click.prevent="goToPage(page)" class="page-link">{{ page }}</a>
                            </li>
                            <li class="paginate_button page-item next" :class="{ disabled: currentPage === totalPages }">
                              <a href="javascript:void(0);" @click.prevent="nextPage" class="page-link">Next</a>
                            </li>
                            <li class="paginate_button page-item" :class="{ disabled: currentPage === totalPages }">
                              <a href="javascript:void(0);" @click.prevent="goToPage(totalPages)" class="page-link">
                                <i class="bx bx-chevrons-right"></i>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modals -->
  <PlanFormModal
      v-if="showPlanFormModal"
      :plan="selectedPlan"
      @close="showPlanFormModal = false"
      @saved="handlePlanSaved"
      @error="handlePlanError"
  />

  <PlanDetailsModal
      v-if="showDetailsModal"
      :plan-id="selectedPlanId"
      @close="showDetailsModal = false"
  />

  <CoverageRulesModal
      v-if="showCoverageRulesModal"
      :plan="selectedPlan"
      @close="showCoverageRulesModal = false"
      @updated="handleRulesUpdated"
  />

  <CoverageConfigModal
      v-if="showConfigModal"
      @close="showConfigModal = false"
      @updated="handleConfigUpdated"
  />

  <!-- Initial Delete Warning Modal -->
  <div class="modal fade" id="deleteWarningModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark border-0">
          <h5 class="modal-title">
            <i class="bx bx-error-circle me-2 fs-4"></i>
            <strong>Delete Plan Warning</strong>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body p-4">
          <div v-if="planToDelete" class="text-center mb-4">
            <div class="mb-3">
              <i class="bx bx-trash text-danger" style="font-size: 64px;"></i>
            </div>
            <h5 class="mb-3">You are about to delete this plan:</h5>
            <div class="card border-danger">
              <div class="card-body bg-light">
                <h4 class="mb-2 text-danger"><strong>{{ planToDelete.plan_name }}</strong></h4>
                <p class="text-muted mb-1">Plan Code: <strong>{{ planToDelete.plan_code }}</strong></p>
                <p class="text-muted mb-0">Type: <span class="badge" :class="getPlanTypeBadgeClass(planToDelete.plan_type)">{{ formatPlanType(planToDelete.plan_type) }}</span></p>
              </div>
            </div>
          </div>

          <div class="alert alert-danger mb-0">
            <div class="d-flex align-items-start">
              <i class="bx bx-error-circle fs-3 me-3"></i>
              <div>
                <h6 class="mb-2"><strong>⚠️ Critical Warning</strong></h6>
                <ul class="mb-0 small">
                  <li>This action <strong>CANNOT</strong> be undone</li>
                  <li>All plan data will be <strong>permanently deleted</strong></li>
                  <li>Active memberships may be affected</li>
                  <li>Historical records will be lost</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-danger" @click="proceedToConfirmation">
            <i class="bx bx-right-arrow-alt me-1"></i>
            Proceed to Confirmation
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal (Step 2) -->
  <div class="modal fade" id="deleteConfirmationModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger text-white border-0">
          <h5 class="modal-title">
            <i class="bx bx-shield-x me-2"></i>
            <strong>Final Confirmation Required</strong>
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body p-4">
          <div v-if="planToDelete" class="mb-4">
            <div class="text-center mb-3">
              <i class="bx bx-error text-danger" style="font-size: 48px;"></i>
            </div>
            <p class="text-center mb-3">You are about to permanently delete:</p>
            <div class="card bg-danger bg-opacity-10 border-danger">
              <div class="card-body text-center p-3">
                <h5 class="mb-1 text-danger"><strong>{{ planToDelete.plan_name }}</strong></h5>
                <p class="text-muted mb-0 small">{{ planToDelete.plan_code }}</p>
              </div>
            </div>
          </div>

          <div class="bg-light p-3 rounded mb-3 border border-warning">
            <p class="mb-2 text-center">
              <i class="bx bx-info-circle text-warning me-1"></i>
              <strong>To proceed with deletion, type the word</strong>
            </p>
            <h4 class="text-center text-danger mb-0">
              <strong>DELETE</strong>
            </h4>
            <p class="text-center text-muted small mb-0 mt-1">in the box below</p>
          </div>

          <div class="mb-3">
            <input
                v-model="deleteConfirmationText"
                type="text"
                class="form-control form-control-lg text-center"
                :class="{'is-invalid': deleteConfirmationText && deleteConfirmationText !== 'DELETE', 'is-valid': deleteConfirmationText === 'DELETE'}"
                placeholder="Type DELETE here"
                @input="deleteConfirmationText = deleteConfirmationText.toUpperCase()"
                autofocus
            />
            <div v-if="deleteConfirmationText && deleteConfirmationText !== 'DELETE'" class="invalid-feedback text-center">
              ❌ You must type DELETE exactly as shown above
            </div>
            <div v-if="deleteConfirmationText === 'DELETE'" class="valid-feedback text-center">
              ✓ Confirmation text is correct
            </div>
          </div>

          <div class="alert alert-danger border-danger mb-0 text-center">
            <i class="bx bx-error-circle me-1"></i>
            <strong>Last chance!</strong> This cannot be undone.
          </div>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button
              type="button"
              class="btn btn-danger btn-lg"
              @click="confirmDeletePlan"
              :disabled="deleteConfirmationText !== 'DELETE' || deleting"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-trash me-2"></i>
            <strong>DELETE PERMANENTLY</strong>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Deactivate Warning Modal (Step 1) -->
  <div class="modal fade" id="deactivateWarningModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark border-0">
          <h5 class="modal-title">
            <i class="bx bx-error-circle me-2 fs-4"></i>
            <strong>Deactivate Plan Warning</strong>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body p-4">
          <div v-if="planToDeactivate" class="text-center mb-4">
            <div class="mb-3">
              <i class="bx bx-x-circle text-warning" style="font-size: 64px;"></i>
            </div>
            <h5 class="mb-3">You are about to deactivate this plan:</h5>
            <div class="card border-warning">
              <div class="card-body bg-light">
                <h4 class="mb-2 text-warning"><strong>{{ planToDeactivate.plan_name }}</strong></h4>
                <p class="text-muted mb-1">Plan Code: <strong>{{ planToDeactivate.plan_code }}</strong></p>
                <p class="text-muted mb-0">Type: <span class="badge" :class="getPlanTypeBadgeClass(planToDeactivate.plan_type)">{{ formatPlanType(planToDeactivate.plan_type) }}</span></p>
              </div>
            </div>
          </div>

          <div class="alert alert-warning mb-0">
            <div class="d-flex align-items-start">
              <i class="bx bx-error-circle fs-3 me-3"></i>
              <div>
                <h6 class="mb-2"><strong>⚠️ Important Notice</strong></h6>
                <ul class="mb-0 small">
                  <li>This plan will become <strong>unavailable</strong> for new enrollments</li>
                  <li>Existing members will retain their coverage</li>
                  <li>You can reactivate this plan later if needed</li>
                  <li>Plan history and data will be preserved</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-warning" @click="proceedToDeactivateConfirmation">
            <i class="bx bx-right-arrow-alt me-1"></i>
            Proceed to Confirmation
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Deactivate Confirmation Modal (Step 2) -->
  <div class="modal fade" id="deactivateConfirmationModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-warning">
        <div class="modal-header bg-warning text-dark border-0">
          <h5 class="modal-title">
            <i class="bx bx-shield-x me-2"></i>
            <strong>Final Confirmation Required</strong>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body p-4">
          <div v-if="planToDeactivate" class="mb-4">
            <div class="text-center mb-3">
              <i class="bx bx-error text-warning" style="font-size: 48px;"></i>
            </div>
            <p class="text-center mb-3">You are about to deactivate:</p>
            <div class="card bg-warning bg-opacity-10 border-warning">
              <div class="card-body text-center p-3">
                <h5 class="mb-1 text-warning"><strong>{{ planToDeactivate.plan_name }}</strong></h5>
                <p class="text-muted mb-0 small">{{ planToDeactivate.plan_code }}</p>
              </div>
            </div>
          </div>

          <div class="bg-light p-3 rounded mb-3 border border-warning">
            <p class="mb-2 text-center">
              <i class="bx bx-info-circle text-warning me-1"></i>
              <strong>To proceed with deactivation, type the word</strong>
            </p>
            <h4 class="text-center text-warning mb-0">
              <strong>DEACTIVATE</strong>
            </h4>
            <p class="text-center text-muted small mb-0 mt-1">in the box below</p>
          </div>

          <div class="mb-3">
            <input
                v-model="deactivateConfirmationText"
                type="text"
                class="form-control form-control-lg text-center"
                :class="{'is-invalid': deactivateConfirmationText && deactivateConfirmationText !== 'DEACTIVATE', 'is-valid': deactivateConfirmationText === 'DEACTIVATE'}"
                placeholder="Type DEACTIVATE here"
                @input="deactivateConfirmationText = deactivateConfirmationText.toUpperCase()"
                autofocus
            />
            <div v-if="deactivateConfirmationText && deactivateConfirmationText !== 'DEACTIVATE'" class="invalid-feedback text-center">
              ❌ You must type DEACTIVATE exactly as shown above
            </div>
            <div v-if="deactivateConfirmationText === 'DEACTIVATE'" class="valid-feedback text-center">
              ✓ Confirmation text is correct
            </div>
          </div>

          <div class="alert alert-warning border-warning mb-0 text-center">
            <i class="bx bx-info-circle me-1"></i>
            <strong>This plan will be deactivated immediately</strong>
          </div>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button
              type="button"
              class="btn btn-warning btn-lg"
              @click="confirmDeactivatePlan"
              :disabled="deactivateConfirmationText !== 'DEACTIVATE' || deactivating"
          >
            <span v-if="deactivating" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-x-circle me-2"></i>
            <strong>DEACTIVATE PLAN</strong>
          </button>
        </div>
      </div>
    </div>
  </div>

  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import PlanFormModal from "@/components/cbhi-plans/modals/PlanFormModal.vue";
import PlanDetailsModal from "@/components/cbhi-plans/modals/PlanDetailsModal.vue";
import CoverageRulesModal from "@/components/cbhi-plans/modals/CoverageRulesModal.vue";
import CoverageConfigModal from "@/components/cbhi-plans/modals/CoverageConfigModal.vue";
import {authUrl, executeJson} from "@/api";

export default {
  components: {
    NavigationBar,
    PlanFormModal,
    PlanDetailsModal,
    CoverageRulesModal,
    CoverageConfigModal
  },

  data() {
    return {
      plans: [],
      filteredPlans: [],
      filterType: 'all',
      loading: true,
      showPlanFormModal: false,
      showDetailsModal: false,
      showCoverageRulesModal: false,
      showConfigModal: false,
      selectedPlan: null,
      selectedPlanId: null,
      planToDelete: null,
      deleteConfirmationText: '',
      deleting: false,
      planToDeactivate: null,
      deactivateConfirmationText: '',
      deactivating: false,
      coverageConfigsInitialized: true,
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',
      planStats: {
        totalPlans: 0,
        activePlans: 0,
        totalMembers: 0,
        totalRevenue: 0
      },
      // Pagination
      currentPage: 1,
      perPage: 10,
      searchTerm: '',
      searchTimeout: null
    };
  },

  computed: {
    // Total plans after filtering
    totalPlans() {
      return this.filteredPlans.length;
    },

    // Total pages
    totalPages() {
      return Math.ceil(this.totalPlans / this.perPage);
    },

    // Paginated plans for current page
    paginatedPlans() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredPlans.slice(start, end);
    },

    // Showing from entry number
    showingFrom() {
      if (this.totalPlans === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    // Showing to entry number
    showingTo() {
      const to = this.currentPage * this.perPage;
      return to > this.totalPlans ? this.totalPlans : to;
    },

    // Visible page numbers for pagination
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      const halfVisible = Math.floor(maxVisible / 2);

      let start = Math.max(1, this.currentPage - halfVisible);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  mounted() {
    this.loadPlans();
    this.loadPlanStats();
    this.checkCoverageConfigs();
  },

  methods: {
    // Data loading methods
    async loadPlans() {
      this.loading = true;
      const data = {
        "function": "getCBHIPlans",
        "searchTerm": this.searchTerm
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.plans = res.data.data.plans || [];
          this.applyFilter();
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to load plans');
      } finally {
        this.loading = false;
      }
    },

    async loadPlanStats() {
      const data = {"function": "getPlanUsageStats"};

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success && res.data.data.plan_stats) {
          const stats = res.data.data.plan_stats;
          this.planStats = {
            totalPlans: this.plans.length,
            activePlans: this.plans.filter(p => p.is_active).length,
            totalMembers: stats.reduce((sum, s) => sum + parseInt(s.active_members || 0), 0),
            totalRevenue: stats.reduce((sum, s) => sum + parseFloat(s.total_potential_revenue || 0), 0)
          };
        }
      } catch (e) {
        console.error('Failed to load plan stats:', e);
      }
    },

    async checkCoverageConfigs() {
      const data = {"function": "getCoverageConfigurations"};

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success && res.data.data.configurations) {
          this.coverageConfigsInitialized = true;
        }
      } catch (e) {
        this.coverageConfigsInitialized = false;
      }
    },

    async initializeConfigs() {
      const data = {"function": "initializeSystemConfigs"};

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', 'Coverage configurations initialized successfully');
          this.coverageConfigsInitialized = true;
        } else {
          this.showAlert('failed', res.data.message || 'Failed to initialize configurations');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to initialize configurations');
      }
    },

    // Search handler with debounce
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1; // Reset to first page on search
        this.applyFilter();
      }, 300);
    },

    // Per page change handler
    handlePerPageChange() {
      this.currentPage = 1; // Reset to first page
    },

    // Refresh plans
    refreshPlans() {
      this.searchTerm = '';
      this.currentPage = 1;
      this.loadPlans();
      this.loadPlanStats();
    },

    // Filter methods
    applyFilter() {
      let filtered = [...this.plans];

      // Apply search filter
      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase();
        filtered = filtered.filter(p =>
            (p.plan_name && p.plan_name.toLowerCase().includes(search)) ||
            (p.plan_code && p.plan_code.toLowerCase().includes(search)) ||
            (p.plan_type && p.plan_type.toLowerCase().includes(search)) ||
            (p.category_name && p.category_name.toLowerCase().includes(search))
        );
      }

      // Apply type filter
      if (this.filterType === 'active') {
        filtered = filtered.filter(p => p.is_active);
      } else if (this.filterType === 'inactive') {
        filtered = filtered.filter(p => !p.is_active);
      } else if (this.filterType !== 'all') {
        filtered = filtered.filter(p => p.plan_type === this.filterType);
      }

      this.filteredPlans = filtered;

      // Reset to first page if current page is out of bounds
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = 1;
      }
    },

    setFilter(type) {
      this.filterType = type;
      this.currentPage = 1; // Reset to first page
      this.applyFilter();
    },

    getFilterText() {
      const filterTexts = {
        'all': 'All Plans',
        'active': 'Active Only',
        'inactive': 'Inactive Only',
        'basic': 'Basic Plans',
        'standard': 'Standard Plans',
        'premium': 'Premium Plans',
        'family': 'Family Plans'
      };
      return filterTexts[this.filterType] || 'All Plans';
    },

    // Pagination methods
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // Modal handlers
    addNewPlan() {
      this.selectedPlan = null;
      this.showPlanFormModal = true;
    },

    editPlan(plan) {
      this.selectedPlan = plan;
      this.showPlanFormModal = true;
    },

    viewPlanDetails(plan) {
      this.selectedPlanId = plan.plan_id;
      this.showDetailsModal = true;
    },

    manageCoverageRules(plan) {
      this.selectedPlan = plan;
      this.showCoverageRulesModal = true;
    },

    openConfigModal() {
      this.showConfigModal = true;
    },

    goToServicesManagement() {
      this.$router.push('/services-management');
    },

    // Plan status methods
    getPlanStatus(plan) {
      if (!plan.is_active) return 'Inactive';

      const now = new Date();
      const effectiveFrom = new Date(plan.effective_from);
      const effectiveTo = plan.effective_to ? new Date(plan.effective_to) : null;

      if (effectiveFrom > now) return 'Pending';
      if (effectiveTo && effectiveTo < now) return 'Expired';
      return 'Active';
    },

    async togglePlanStatus(plan) {
      // If plan is inactive, activate it directly (no confirmation needed)
      if (!plan.is_active) {
        await this.activatePlan(plan);
        return;
      }

      // If plan is active, show deactivation warning modal
      this.planToDeactivate = plan;
      this.deactivateConfirmationText = '';
      this.deactivating = false;

      const warningModal = new bootstrap.Modal(document.getElementById('deactivateWarningModal'));
      warningModal.show();
    },

    async activatePlan(plan) {
      const data = {
        "function": "activateCBHIPlan",
        "planId": plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `Plan "${plan.plan_name}" activated successfully`);
          this.loadPlans();
          this.loadPlanStats();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to activate plan');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to activate plan');
      }
    },

    proceedToDeactivateConfirmation() {
      // Hide the warning modal
      const warningModal = bootstrap.Modal.getInstance(document.getElementById('deactivateWarningModal'));
      if (warningModal) {
        warningModal.hide();
      }

      // Show the confirmation modal after a brief delay
      setTimeout(() => {
        const confirmModal = new bootstrap.Modal(document.getElementById('deactivateConfirmationModal'));
        confirmModal.show();
      }, 300);
    },

    async confirmDeactivatePlan() {
      if (!this.planToDeactivate || this.deactivateConfirmationText !== 'DEACTIVATE') return;

      this.deactivating = true;

      const data = {
        "function": "deactivateCBHIPlan",
        "planId": this.planToDeactivate.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `Plan "${this.planToDeactivate.plan_name}" deactivated successfully`);
          this.loadPlans();
          this.loadPlanStats();

          const modal = bootstrap.Modal.getInstance(document.getElementById('deactivateConfirmationModal'));
          if (modal) {
            modal.hide();
          }

          this.planToDeactivate = null;
          this.deactivateConfirmationText = '';
        } else {
          this.showAlert('failed', res.data.message || 'Failed to deactivate plan');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to deactivate plan');
      } finally {
        this.deactivating = false;
      }
    },

    // Plan deletion - Two-step process
    deletePlan(plan) {
      this.planToDelete = plan;
      this.deleteConfirmationText = '';
      this.deleting = false;

      // Show the initial warning modal
      const warningModal = new bootstrap.Modal(document.getElementById('deleteWarningModal'));
      warningModal.show();
    },

    proceedToConfirmation() {
      // Hide the warning modal
      const warningModal = bootstrap.Modal.getInstance(document.getElementById('deleteWarningModal'));
      if (warningModal) {
        warningModal.hide();
      }

      // Show the confirmation modal after a brief delay
      setTimeout(() => {
        const confirmModal = new bootstrap.Modal(document.getElementById('deleteConfirmationModal'));
        confirmModal.show();
      }, 300);
    },

    async confirmDeletePlan() {
      if (!this.planToDelete || this.deleteConfirmationText !== 'DELETE') return;

      this.deleting = true;

      const data = {
        "function": "deleteCBHIPlan",
        "planId": this.planToDelete.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `Plan "${this.planToDelete.plan_name}" deleted successfully`);
          this.loadPlans();
          this.loadPlanStats();

          const modal = bootstrap.Modal.getInstance(document.getElementById('deleteConfirmationModal'));
          if (modal) {
            modal.hide();
          }

          this.planToDelete = null;
          this.deleteConfirmationText = '';
        } else {
          this.showAlert('failed', res.data.message || 'Failed to delete plan');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to delete plan');
      } finally {
        this.deleting = false;
      }
    },

    // Event handlers
    handlePlanSaved(event) {
      this.showAlert('success', event.message || 'Plan saved successfully');
      this.loadPlans();
      this.loadPlanStats();
    },

    handlePlanError(event) {
      this.showAlert('failed', event.message || 'Error saving plan');
    },

    handleConfigUpdated(event) {
      this.showAlert('success', event.message || 'Configuration updated successfully');
      this.loadCoverageConfigs();
      this.loadPlans();
    },

    handleRulesUpdated(event) {
      this.showAlert('success', event.message || 'Coverage rules updated successfully');
      this.loadPlans();
    },

    // UI Helper Methods
    getPlanIcon(planType) {
      const iconMap = {
        'basic': 'bx bxs-shield',
        'standard': 'bx bxs-shield-alt-2',
        'premium': 'bx bxs-badge-check',
        'family': 'bx bxs-group',
        'corporate': 'bx bxs-buildings',
        'lower': 'bx bxs-shield',
        'medium': 'bx bxs-shield-alt-2',
        'upper': 'bx bxs-badge-check'
      };
      return iconMap[planType?.toLowerCase()] || 'bx bxs-shield';
    },

    getPlanIconWrapperClass(planType) {
      const normalizedType = planType?.toLowerCase();
      const classMap = {
        'basic': 'icon-basic',
        'standard': 'icon-standard',
        'premium': 'icon-premium',
        'family': 'icon-family',
        'corporate': 'icon-corporate',
        'lower': 'icon-basic',
        'medium': 'icon-standard',
        'upper': 'icon-premium'
      };
      return classMap[normalizedType] || 'icon-basic';
    },

    getPlanTypeBadgeClass(planType) {
      const classMap = {
        'basic': 'bg-secondary',
        'standard': 'bg-primary',
        'premium': 'bg-success',
        'family': 'bg-info',
        'corporate': 'bg-warning'
      };
      return classMap[planType] || 'bg-secondary';
    },

    formatPlanType(planType) {
      return planType ? planType.charAt(0).toUpperCase() + planType.slice(1) : '';
    },

    getStatusBadgeClass(plan) {
      const status = this.getPlanStatus(plan);
      const classMap = {
        'Active': 'bg-success',
        'Inactive': 'bg-danger',
        'Expired': 'bg-warning',
        'Pending': 'bg-info'
      };
      return classMap[status] || 'bg-secondary';
    },

    getStatusIcon(plan) {
      const status = this.getPlanStatus(plan);
      const iconMap = {
        'Active': 'bx bx-check-circle me-1',
        'Inactive': 'bx bx-x-circle me-1',
        'Expired': 'bx bx-time me-1',
        'Pending': 'bx bx-hourglass me-1'
      };
      return iconMap[status] || 'bx bx-question-mark me-1';
    },

    // Utility methods
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    // Alert management
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.spinner-border {
  width: 3rem;
  height: 3rem;
}

.avatar-sm {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
}

/* Search box styles */
.search-box .form-control {
  border-radius: 30px;
  padding-left: 40px;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #74788d;
}

/* Spin animation for refresh button */
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Plan Icon Styling */
.plan-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.plan-icon-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.plan-icon {
  font-size: 26px !important;
  z-index: 2;
  position: relative;
  display: flex !important;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* Basic Plan - Gray */
.icon-basic {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
}

.icon-basic .plan-icon {
  color: #ffffff !important;
}

/* Standard Plan - Blue */
.icon-standard {
  background: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
}

.icon-standard .plan-icon {
  color: #ffffff !important;
}

/* Premium Plan - Green/Gold */
.icon-premium {
  background: linear-gradient(135deg, #1cc88a 0%, #13855c 100%);
}

.icon-premium .plan-icon {
  color: #ffffff !important;
}

/* Family Plan - Teal */
.icon-family {
  background: linear-gradient(135deg, #36b9cc 0%, #258391 100%);
}

.icon-family .plan-icon {
  color: #ffffff !important;
}

/* Corporate Plan - Orange */
.icon-corporate {
  background: linear-gradient(135deg, #f6c23e 0%, #d4a017 100%);
}

.icon-corporate .plan-icon {
  color: #ffffff !important;
}

.bg-soft-primary {
  background-color: rgba(78, 115, 223, 0.1);
  color: #4e73df;
}

.bg-soft-secondary {
  background-color: rgba(133, 135, 150, 0.1);
  color: #858796;
}

.bg-soft-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.border-left-info {
  border-left: 4px solid #17a2b8;
}

.border-left-danger {
  border-left: 4px solid #dc3545;
}

.pricing-details {
  min-width: 200px;
}

.family-coverage-info {
  min-width: 220px;
}

.eligibility-info .alert {
  margin-bottom: 0;
  padding: 0.5rem;
}

.border-left {
  border-left-width: 3px !important;
}

/* Pagination styles */
.pagination {
  margin-bottom: 0;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

.page-link {
  color: #556ee6;
}

.page-link:hover {
  color: #4458cb;
}

/* Enhanced delete confirmation input styles */
.form-control.is-valid {
  border-color: #28a745;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
}

.form-control.is-invalid {
  border-color: #dc3545;
}

/* DataTables info styling */
.dataTables_info {
  padding-top: 0.5rem;
  color: #74788d;
}
</style>